import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, Boxes, Cpu, Radar as RadarIcon, ArrowUpRight, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";
import { money, pct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { RiskBadge } from "@/components/shared/Controls";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground">{label}</span>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent || "bg-primary/10"}`}>
          <Icon className="h-4 w-4 text-primary" />
        </div>
      </div>
      <p className="font-heading text-2xl font-bold" data-testid={`stat-${label}`}>{value}</p>
    </div>
  );
}

export default function Overview({ onNavigate }) {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const cap = data.capital || {};
  const cur = cap.currency || "BRL";
  const chart = [
    { name: "Inicial", v: cap.available + cap.invested || 0 },
    { name: "Investido", v: cap.invested || 0 },
    { name: "Vendas", v: cap.sales_revenue || 0 },
    { name: "Custos", v: cap.costs || 0 },
    { name: "Lucro", v: cap.net_profit_est || 0 },
    { name: "Disponível", v: cap.available || 0 },
  ];

  return (
    <div className="space-y-6 animate-float-up">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Capital disponível" value={money(cap.available, cur)} />
        <Stat icon={Boxes} label="Capital investido" value={money(cap.invested, cur)} />
        <Stat icon={TrendingUp} label="Lucro estimado" value={money(cap.net_profit_est, cur)} />
        <Stat icon={Cpu} label="Agentes ativos" value={`${data.agents.active}/${data.agents.total}`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Crescimento do capital</h3>
            <span className="text-xs font-mono text-muted-foreground">{cur}</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={40} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Area type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-heading font-semibold mb-3">Oportunidades</h3>
            <div className="space-y-2 text-sm">
              {[["Novas", data.opportunities.new], ["Analisadas", data.opportunities.analyzed], ["Aprovadas", data.opportunities.approved], ["Descartadas", data.opportunities.discarded], ["Compradas", data.opportunities.purchased]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono font-semibold">{v}</span></div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => onNavigate("opportunities")} data-testid="go-opportunities">Ver oportunidades <ArrowUpRight className="h-3.5 w-3.5 ml-1" /></Button>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-2"><Globe className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Mercados</h3></div>
            <p className="text-sm text-muted-foreground">Países analisados: <span className="font-mono font-semibold text-foreground">{data.markets.countries_analyzed}</span></p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center pulse-ring"><RadarIcon className="h-5 w-5 text-primary" /></div>
        <div className="flex-1">
          <p className="font-heading font-semibold">Radar Global de Oportunidades</p>
          <p className="text-sm text-muted-foreground"><span className="font-mono text-amber-500">INTEGRAÇÃO NECESSÁRIA</span> — conecte APIs de marketplaces para varredura automática. Enquanto isso, adicione oportunidades manualmente e deixe a IA analisá-las.</p>
        </div>
        <Button size="sm" onClick={() => onNavigate("opportunities")} data-testid="overview-add-opp">Adicionar oportunidade</Button>
      </div>

      {data.alerts.recent.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold mb-3">Alertas recentes</h3>
          <div className="space-y-2">
            {data.alerts.recent.map((a) => (
              <div key={a.alert_id} className="flex items-center gap-3 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="flex-1">{a.message}</span>
                {a.type === "margin" && <RiskBadge level="BAIXO" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
